import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AggregateBucket } from '@/types';
import type { TodBucket } from '@/lib/aggregations';
import { TOD_COLORS, type ToDPeriod } from '@/config/constants';

export interface ActivePowerBarChartProps {
  buckets:    AggregateBucket[];
  todBuckets: TodBucket[];
  mode:       'kw' | 'kwh';
  unit?:      string;   // energy unit label shown in tooltip — defaults to 'kWh'
  height?:    number;
}

const TOD_LABEL: Record<ToDPeriod, string> = {
  'OFF-PEAK': 'Off-Peak',
  'NORMAL':   'Normal',
  'PEAK':     'Peak',
};

/**
 * Given a raw maximum data value, returns { max, ticks } where:
 * - max  is a clean rounded ceiling above rawMax
 * - ticks is an array of 5–6 evenly-spaced values from 0 to max
 */
function niceYAxis(rawMax: number, tickCount = 5): { max: number; ticks: number[] } {
  if (rawMax <= 0) {
    const ticks = Array.from({ length: tickCount }, (_, i) => i * 20);
    return { max: (tickCount - 1) * 20, ticks };
  }

  // Choose a "nice" step that makes the axis readable
  const rawStep = rawMax / (tickCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;

  let niceStep: number;
  if      (normalized <= 1)   niceStep = 1   * magnitude;
  else if (normalized <= 2)   niceStep = 2   * magnitude;
  else if (normalized <= 2.5) niceStep = 2.5 * magnitude;
  else if (normalized <= 5)   niceStep = 5   * magnitude;
  else                         niceStep = 10  * magnitude;

  const niceMax = Math.ceil(rawMax / niceStep) * niceStep;
  const ticks   = Array.from({ length: tickCount }, (_, i) => +(i * (niceMax / (tickCount - 1))).toFixed(6));

  return { max: niceMax, ticks };
}

const TOD_ORDER: ToDPeriod[] = ['OFF-PEAK', 'NORMAL', 'PEAK'];

/**
 * Dynamic symmetric x-axis tick selector — picks `targetCount` evenly-spaced
 * labels from the data array so ticks are visually symmetric regardless of
 * how many bars are shown. Always includes first and last.
 */
function symmetricXTicks(data: { label: string }[], targetCount = 5): string[] {
  const n = data.length;
  if (n === 0) return [];
  if (n <= targetCount) return data.map(d => d.label);
  const seen = new Set<string>();
  const ticks: string[] = [];
  for (let i = 0; i < targetCount; i++) {
    const idx   = Math.round(i * (n - 1) / (targetCount - 1));
    const label = data[idx].label;
    if (!seen.has(label)) { seen.add(label); ticks.push(label); }
  }
  return ticks;
}

/* ── Tick helpers ────────────────────────────────────────────────────────── */

function fmtY(v: number): string {
  if (v >= 10_000) return `${(v / 1000).toFixed(0)}k`;
  if (v >= 1_000)  return `${(v / 1000).toFixed(1)}k`;
  if (v >= 100)    return v.toFixed(0);
  if (v >= 10)     return v.toFixed(1);
  return v.toFixed(2);
}

function XTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) {
  if (x == null || y == null) return null;
  return (
    <text x={x} y={y + 12} textAnchor="middle" fontSize={12} fontWeight="800" fill="#0F172A" fontFamily="inherit">
      {payload?.value}
    </text>
  );
}

function YTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: number } }) {
  if (x == null || y == null) return null;
  return (
    <text x={x - 6} y={y + 4} textAnchor="end" fontSize={11} fontWeight="700" fill="#64748B" fontFamily="inherit">
      {fmtY(payload?.value ?? 0)}
    </text>
  );
}

function TodLegend() {
  return (
    <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-1 mt-3 text-[10px] font-semibold text-ink-900">
      {TOD_ORDER.map((p) => (
        <span key={p} className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: TOD_COLORS[p] }} />
          {TOD_LABEL[p]}
        </span>
      ))}
    </div>
  );
}

/* ── Empty skeleton — axes + grid, no data ──────────────────────────────── */

const EMPTY_SKELETON: TodBucket[] = ['06:00', '12:00', '18:00', '00:00'].map(label => ({
  label, offPeak: 0, normal: 0, peak: 0, ts: 0,
}));

function EmptyBarChart({ height }: { height: number }) {
  return (
    <>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <BarChart data={EMPTY_SKELETON} margin={{ top: 8, right: 4, left: 4, bottom: 2 }} barCategoryGap="30%">
            <CartesianGrid stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={<XTick />} />
            <YAxis tickLine={false} axisLine={false} width={48}
              domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={<YTick />} />
            <Bar dataKey="offPeak" stackId="tod" fill="transparent" />
            <Bar dataKey="normal"  stackId="tod" fill="transparent" />
            <Bar dataKey="peak"    stackId="tod" fill="transparent" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <TodLegend />
    </>
  );
}

/* ── Real charts ─────────────────────────────────────────────────────────── */

function KwBarChart({ todBuckets, height, unit = 'kWh' }: { todBuckets: TodBucket[]; height: number; unit?: string }) {
  if (!todBuckets.length) return <EmptyBarChart height={height} />;

  const maxTotal       = Math.max(...todBuckets.map(b => b.offPeak + b.normal + b.peak), 1);
  const { max: yHigh, ticks: yTicks } = niceYAxis(maxTotal, 5);
  const axisTicks = symmetricXTicks(todBuckets, 5);

  return (
    <>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <BarChart data={todBuckets} margin={{ top: 8, right: 4, left: 4, bottom: 2 }} barCategoryGap="30%">
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} ticks={axisTicks} interval={0} tick={<XTick />} />
            <YAxis tickLine={false} axisLine={false} width={48} domain={[0, yHigh]} ticks={yTicks} tick={<YTick />} />
            <Tooltip
              cursor={{ fill: 'rgba(15,177,138,0.07)' }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload as TodBucket;
                // Pick the single ToD key with the largest value — only one
                // period is active in any given hour, so the others will be 0.
                // We select by max value rather than filtering > 0 so the
                // tooltip always shows exactly one row (never zero rows).
                const keys = ['offPeak', 'normal', 'peak'] as const;
                const best = keys.reduce<{ key: typeof keys[number]; val: number }>(
                  (top, k) => {
                    const v = d?.[k] ?? 0;
                    return v > top.val ? { key: k, val: v } : top;
                  },
                  { key: 'normal', val: -1 },
                );
                // Nothing to show if the bar is truly empty
                if (best.val <= 0) return null;
                const tod = best.key === 'offPeak' ? 'OFF-PEAK' : best.key === 'normal' ? 'NORMAL' : 'PEAK';
                return (
                  <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[150px]">
                    <div className="font-bold text-slate-900 mb-1.5">{label}</div>
                    <div className="flex justify-between gap-4">
                      <span className="flex items-center gap-1.5 font-semibold" style={{ color: TOD_COLORS[tod] }}>
                        <span className="inline-block w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: TOD_COLORS[tod] }} />
                        {TOD_LABEL[tod]}
                      </span>
                      <span className="num font-bold text-slate-900">{best.val.toFixed(3)} {unit}</span>
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="offPeak" stackId="tod" fill={TOD_COLORS['OFF-PEAK']} radius={[0,0,0,0]} maxBarSize={64} />
            <Bar dataKey="normal"  stackId="tod" fill={TOD_COLORS['NORMAL']}   radius={[0,0,0,0]} maxBarSize={64} />
            <Bar dataKey="peak"    stackId="tod" fill={TOD_COLORS['PEAK']}     radius={[4,4,0,0]} maxBarSize={64} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <TodLegend />
    </>
  );
}

function KwhStackedChart({ todBuckets, height, unit = 'kWh' }: { todBuckets: TodBucket[]; height: number; unit?: string }) {
  if (!todBuckets.length) return <EmptyBarChart height={height} />;

  const maxTotal = Math.max(...todBuckets.map(b => b.offPeak + b.normal + b.peak), 1);
  const { max: yHigh, ticks: yTicks } = niceYAxis(maxTotal, 5);

  return (
    <>
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer>
          <BarChart data={todBuckets} margin={{ top: 8, right: 4, left: 4, bottom: 2 }} barCategoryGap="30%">
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} ticks={symmetricXTicks(todBuckets, 5)} interval={0} tick={<XTick />} />
            <YAxis tickLine={false} axisLine={false} width={48} domain={[0, yHigh]} ticks={yTicks} tick={<YTick />} />
            <Tooltip
              cursor={{ fill: 'rgba(15,177,138,0.07)' }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload as TodBucket;
                const total = (d?.offPeak ?? 0) + (d?.normal ?? 0) + (d?.peak ?? 0);
                return (
                  <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[170px]">
                    <div className="font-bold text-slate-900 mb-1.5">{label}</div>
                    <div className="space-y-0.5">
                      {(['offPeak','normal','peak'] as const).map(key => {
                        const tod = key === 'offPeak' ? 'OFF-PEAK' : key === 'normal' ? 'NORMAL' : 'PEAK';
                        const val = d?.[key] ?? 0;
                        if (val <= 0) return null;
                        return (
                          <div key={key} className="flex justify-between gap-4">
                            <span className="flex items-center gap-1.5 font-semibold" style={{ color: TOD_COLORS[tod] }}>
                              <span className="inline-block w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: TOD_COLORS[tod] }} />
                              {TOD_LABEL[tod]}
                            </span>
                            <span className="num font-bold text-slate-900">{(val as number).toFixed(2)} {unit}</span>
                          </div>
                        );
                      })}
                      <div className="flex justify-between gap-4 border-t border-slate-100 pt-1 mt-1">
                        <span className="font-semibold text-slate-500">Total</span>
                        <span className="num font-bold text-slate-900">{total.toFixed(2)} {unit}</span>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="offPeak" stackId="tod" fill={TOD_COLORS['OFF-PEAK']} radius={[0,0,0,0]} maxBarSize={64} />
            <Bar dataKey="normal"  stackId="tod" fill={TOD_COLORS['NORMAL']}   radius={[0,0,0,0]} maxBarSize={64} />
            <Bar dataKey="peak"    stackId="tod" fill={TOD_COLORS['PEAK']}     radius={[4,4,0,0]} maxBarSize={64} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <TodLegend />
    </>
  );
}

export function ActivePowerBarChart({ buckets: _buckets, todBuckets, mode, unit = 'kWh', height = 220 }: ActivePowerBarChartProps) {
  const safeBuckets = todBuckets ?? [];
  if (mode === 'kw') return <KwBarChart  todBuckets={safeBuckets} height={height} unit={unit} />;
  return <KwhStackedChart todBuckets={safeBuckets} height={height} unit={unit} />;
}